import { store } from '../store'
import { getCustomer } from './utils'

/**
 * This key name is used to store a bot window reference in local storage.
 * This object is then accessed everytime we load this container to check if a reference is
 * already present. If not, a new reference is created when bot icon is clicked and stored in local storage.
 */
const WINDOW_KEY = 'botWindowRef'

/**
 * Opens a chatbot in a new popout window or reuses an existing one if it's still open.
 *
 * This function handles opening a chatbot window with configurable position and size.
 * It first checks if a chatbot window (`window.botWindowReference`) already exists and
 * is still open. If yes, it reuses that window and focuses it. Otherwise, it opens a
 * new window with the specified configuration and stores its name in `localStorage`
 * under `WINDOW_KEY` for persistence across refreshes.
 * @param {string} url - The URL to be opened in the bot window.
 * @param {string} customer - The name of the customer for which the bot needs to be mounted.
 * @param {Object} [tabConfig={}] - Optional configuration for window position and size.
 * @param {number} [tabConfig.topOffset=5] - The vertical offset (in pixels) from the top of the screen.
 * @param {number} [tabConfig.leftOffset=200] - The horizontal offset (in pixels) from the left of the screen.
 * @param {number} [tabConfig.windowHeight=800] - The height (in pixels) of the popout window.
 * @param {number} [tabConfig.windowWidth=450] - The width (in pixels) of the popout window.
 */
export const handleBotWindowReference = (url, customer, tabConfig = {}) => {
  try {
    let newWindow
    const windowName = 'newBotWindow'
    const {
      topOffset = 5,
      leftOffset = 200,
      windowHeight = 800,
      windowWidth = 450
    } = tabConfig
    const windowFeatures = `width=${windowWidth},height=${windowHeight},left=${leftOffset},top=${topOffset},resizable=yes,scrollbars=yes`

    if (window.botWindowReference && !window.botWindowReference.closed) {
      newWindow = window.botWindowReference
      newWindow.focus()
      window.isNewBotWindow = false
    } else {
      newWindow = window.open(`${url}/?customer=${customer}`, windowName, windowFeatures)
      window.botWindowReference = newWindow
      window.isNewBotWindow = true
      window.localStorage.setItem(WINDOW_KEY, newWindow.name)
    }
  } catch (error) {
    console.log('Error handling window reference:', error)
  }
}

/**
 * Handles the management of an existing bot window in the browser. (if still open)
 *
 * This function checks the browser's `localStorage` for a stored window name (via `WINDOW_KEY`)
 * and attempts to reconnect to that named window — but **without** unintentionally
 * opening a new blank window on page refresh.
 *
 */
export const handleExistingBotWindow = () => {
  const existingWindowName = window.localStorage.getItem(WINDOW_KEY)
  if (!existingWindowName) return
  if (window.botWindowReference && !window.botWindowReference.closed) {
    return
  }
  let existingWindow
  try {
    existingWindow = window.open('', existingWindowName)
    if (existingWindow && existingWindow.closed) {
      throw new Error('Closed window')
    }
    if (existingWindow && existingWindow.location && existingWindow.location.href === 'about:blank') {
      throw new Error('Blank window')
    }
    window.botWindowReference = existingWindow
  } catch (error) {
    window.localStorage.removeItem(WINDOW_KEY)
    if (existingWindow && !existingWindow.closed) {
      existingWindow.close()
    }
  }
}

/**
 * Posts a message to the bot window.
 *
 * @param {object} data - The data to send to the bot window.
 * @param {string} targetOrigin - The target origin for the postMessage call.
 * @returns {void}
 *
 * @example
 * postMessageToBotWindow('hello', 'https://example.com'); // { event: message }
 *
 * @remarks
 * If the message is 'confirmLogout', it indicates that the user is logged out, and the bot window reference will be set to null, causing it to close.
 * @see https://github.com/devicebits/qnabot-web-widget/src/lex-web-ui-loader/js/lib/fullpage-component-loader.js
 */
export const postMessageToBotWindow = (data, targetOrigin) => {
  window.botWindowReference.postMessage(data, targetOrigin);
  if (data.event === 'confirmLogout') {
    window.botWindowReference = null;
  }
}

/**
 * Pushes the active call session id into the bot so its analytics events are
 * attributed to the right call, on whichever channel is in use: the embedded
 * loader (floating / sidebar iframe) or the popped-out bot window.
 *
 * @param {string} [callSessionId=''] - The active call session id, or '' to
 *   clear it once the call has ended.
 * @returns {void}
 */
export const sendCallSessionToBot = (callSessionId = '') => {
  const message = { event: 'CALL_SESSION_ID', call_session_id: callSessionId }
  window.botInstance?.sendMessage(message)
  if (window.botWindowReference) {
    postMessageToBotWindow(message, '*')
  }
}

/**
 * Generates a script tag that loads the Lex Web UI loader script from the provided baseUrl.
 * The script tag is appended to the document body and initializes the Lex Web UI loader when the script is loaded.
 *
 * @see https://github.com/devicebits/qnabot-web-widget#lex-web-ui-for-qnabot-on-aws
 * @param {string} baseUrl - The base URL where the Lex Web UI loader script is hosted.
 * @param {string} customer - The name of the customer whose configuration needs to be fetched from database.
 * @returns {string} The generated script tag as a string.
 */
export const  createLexWebUiScriptTag = (baseUrl, customer) => {
  return `
  <script type="text/javascript">
    var lexScript = document.createElement("script");
    lexScript.src = "${baseUrl}/lex-web-ui-loader.js";
    lexScript.type = "text/javascript";

    lexScript.addEventListener("load", () => {
      var loaderOpts = {
        baseUrl: "${baseUrl}",
        shouldLoadMinDeps: true,
      };
      var loader = new ChatBotUiLoader.IframeLoader(loaderOpts);
      loader.load({
        lex: {
          sessionAttributes: 
            {
              customer: "${customer}"
            }
        },
        ui: {
          parentOrigin: "${window.location.origin}"
        },
        iframe: {
          iframeOrigin: "${baseUrl}"
        }
      }).catch(function (error) {
        console.error(error);
      });
    });
    document.body.append(lexScript);
  </script>
  `;
}

/**
 * Handles deeplink events by parsing the URL query parameters and extracting the 'showBot' parameter.
 */
export const handleDeeplink = () => {
  const urlParams = window.location.search
  const showBotMatch = urlParams.match(/[?&]showBot=([^&]*)/)
  const showBot = showBotMatch ? decodeURIComponent(showBotMatch[1]) : null

  if (showBot) {
    const { isQNA, windowed, baseUrl } = store.getState().devicebits.options.bot
    const customer = getCustomer()
    const event = 'deeplink'

    const data = {
      event,
      detail: {
        module: showBot
      }
    }

    if (isQNA) {
      /**
       * If the bot is in windowed mode, it will open the standalone full page bot and send the message via postMessage.
       * If the bot is in iframe mode, it will dispatch a custom event to the document to send the message to the bot.
       */
      if (windowed) {
        handleBotWindowReference(baseUrl, customer)
        if(window.isNewBotWindow) {
          // the bot needs time to load its own listeners before it can receive the message
          setTimeout(() => { postMessageToBotWindow(data, '*') }, 8000)
        } else {
          postMessageToBotWindow(data, '*')
        }
      } else {
        document.dispatchEvent(new CustomEvent(
          'lexWebUiMessage',
          { detail: { message: { event, message: showBot } } }
        ))
      }
    }
  }
}

/**
 * Loads a Reactbot iframe by dynamically injecting the bot loader script
 * @param {string} baseUrl - The base URL for the ReactBot
 * @param {string} customer - The customer name
 * @param {boolean} [requireCallSession=false] - When true the bot loads but holds
 *   back its opening module request until a call session exists and the agent has
 *   actually opened the chat, so nothing is logged against a null call_session_id.
 *   Driven by `callHandler.enabled`, since only call-handling apps have sessions.
 * @returns {void}
 */

export const loadReactBot = (baseUrl, customer, requireCallSession = false) => {
  const loaderScript = document.createElement('script');
  loaderScript.src = `${baseUrl}/iframe.js`;
  loaderScript.id = 'bot-loader-script';

  loaderScript.onload = function () {
    if (window.BotLoader?.IframeLoader) {
      window.botInstance = new window.BotLoader.IframeLoader({
        baseUrl: `${baseUrl}`,
      });

      window.botInstance.load({
        lex: {
          sessionAttributes: { customer },
        },
        ui: { requireCallSession },
      });
    } else {
      console.error('BotLoader.IframeLoader not found');
    }
  };
  document.body.appendChild(loaderScript);
}
